import * as React from "react";
import CreateProject from "@app/projects/create/createProject";
import ResourceService from "@app/resources/resourceService";
import WizardStep from "@app/projects/create/WizardStep";
import { ResourceTagModel } from "@app/tags/import/types";
import TagResource from "@app/tags/TagResource";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel, Perspectives } from "@app/tags/types";
import { fromNullable } from "@app/shared/utility/fromNullable";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

interface CreateProjectSetupProps {
  createProjectModel: CreateProject;
  baseUrl: string;
  onNextClick: () => void;
  progressList: string[];
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDescChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleIsPrivateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTaggingChange: (tags: ResourceTagModel[]) => void;
  currentIndex: number;
}

const resourceTaggingService = new ResourceTaggingService();
const resourceService = new ResourceService();
const nonbreakingspace = "\xa0";

const CreateProjectSetup: React.FC<React.PropsWithChildren<CreateProjectSetupProps>> = (props: CreateProjectSetupProps) => {
  const [descriptionError, setDescriptionError] = React.useState(nonbreakingspace);
  const [titles, setTitles] = React.useState<string[]>(null);
  const [isNextDisabled, setIsNextDisabled] = React.useState<boolean>(() => handleIsNextDisabled(props.createProjectModel.name, props.createProjectModel.description, props.createProjectModel.tags, titles));
  const [nameError, setNameError] = React.useState(nonbreakingspace);
  const [resourceTags, setResourceTags] = React.useState<ManageResourceTagDisplayModel[]>([]);
  
  const user = React.useContext(UserContext);
  const userIsResourceAdmin = user.roles.includes(Roles.CLIENT_ADMIN);
  
  React.useEffect(() => {
    resourceService.getProjectNames()
      .then(res => setTitles(res));
      
    if (userIsResourceAdmin) {
      resourceTaggingService.getClientProjectTagInfo(Perspectives.All)
        .then(res => setResourceTags(res));
    }
  }, []);

  React.useEffect(() => {
    setIsNextDisabled(handleIsNextDisabled(props.createProjectModel.name, props.createProjectModel.description, props.createProjectModel.tags, titles));
  }, [titles, props.createProjectModel.name, props.createProjectModel.description, props.createProjectModel.tags]);

  const projectNameOk = (props.createProjectModel.name !== null && props.createProjectModel.name !== "" && (!titles || !titles.includes(props.createProjectModel.name)));
  const projectDescriptionOk = (props.createProjectModel.description !== null && props.createProjectModel.description !== "");

  const onTaggingChange = (updatedTags: ResourceTagModel[]): void => {
    props.handleTaggingChange(updatedTags);
    setIsNextDisabled(handleIsNextDisabled(props.createProjectModel.name, props.createProjectModel.description, updatedTags, titles));
  }

  return (
    <WizardStep {...props} onNextClick={props.onNextClick} heading="Create a New Project" subheading="Name and describe your new project..." baseUrl={props.baseUrl} nextUrl={(props.currentIndex + 1).toString()} progressList={props.progressList} isNextDisabled={isNextDisabled}>
      <div className={`form-group row ${fromNullable(projectNameOk, "", "has-error", "has-error")}`}>
        <div className="col-6 offset-2">
          <QESTextInput
            label="Name"
            placeholder="Project Name"
            maxLength={255}
            value={props.createProjectModel.name}
            onChange={(e) => { props.handleNameChange(e); validateName(e.target.value, titles, setNameError); }}
            onBlur={() => validateDescription(props.createProjectModel.name, setNameError)}
            testId="create-project-step-setup__create-project-name-input"
            descriptiveText={!projectNameOk && nameError}
            required={true}
            isValid={projectNameOk}
          />
        </div>
      </div>
      <div className={`form-group row ${fromNullable(projectDescriptionOk, "", "has-error", "has-error")}`}>
        <div className="col-6 offset-2">
          <QESTextInput
            label="Description"
            placeholder="Project Description"
            maxLength={255}
            value={props.createProjectModel.description}
            onChange={(e) => { props.handleDescChange(e); validateDescription(e.target.value, setDescriptionError); }}
            onBlur={() => validateDescription(props.createProjectModel.description, setDescriptionError)}
            testId="create-project-step-setup__create-project-description-input"
            descriptiveText={!projectDescriptionOk && descriptionError}
            required={true}
            isValid={projectDescriptionOk}
          />
        </div>
      </div>
      <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
        <div className="form-group row">
          <TagResource
            resourceTags={resourceTags}
            tags={props.createProjectModel.tags && props.createProjectModel.tags.length > 0 ? props.createProjectModel.tags : [{ tagGroupName: "", tagName: "" } as ResourceTagModel]}
            handleTaggingChange={onTaggingChange}
          />
        </div>
      </RoleRestricted>
      <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
        <div className="form-group row">
          <div className="col-6 offset-2">
            <QESCheckbox
              containerClassName="mt-0"
              label="This is a Private Project (can be administered only by you)"
              checked={props.createProjectModel.isPrivate}
              data-testid="create-project-step-setup__create-project-private-project-checkbox"
              onChange={(e) => props.handleIsPrivateChange(e)}
              testId="create-project__private"
            />
          </div>
        </div>
      </RoleRestricted>
    </WizardStep>
  );
};

const handleIsNextDisabled = (name: string, description: string, tags: ResourceTagModel[], titles: string[]) => {
  return !(
    // Step 1
    name !== "" &&
    description !== "" &&
    (tags.every(tag => tag.tagName.length === 0 && tag.tagGroupName.length === 0) 
      || tags.every(tag => tag.tagName.length > 0 && tag.tagGroupName.length > 0)) &&
    !!titles &&
    (titles.length === 0 ||
      !titles.includes(name))
  );
};

const validateName = (name: string, titles: string[], setNameError: React.Dispatch<React.SetStateAction<string>>) => {
  if (!name || name.length === 0) {
    setNameError("Name must not be empty.");
  } else if (titles?.includes(name)) {
    setNameError("This name is in use by another project.");
  } else {
    setNameError(nonbreakingspace);
  }
};

const validateDescription = (description: string, setDescriptionError: React.Dispatch<React.SetStateAction<string>>) => {
  if (!description || description.length === 0) {
    setDescriptionError("Description must not be empty.");
  } else {
    setDescriptionError(nonbreakingspace);
  }
};

export default CreateProjectSetup;
